import { api } from './apiClient';

/**
 * Typed client for the Fixed Asset Management module — `/api/v1/assets`.
 *
 * Every apiClient verb auto-unwraps `{status, data}` to `data` (see
 * apiClient.ts). Most of these controllers nest the payload one level
 * deeper under a named key (e.g. `data: { asset }`); a few return the
 * computed object directly (summary, preview, disposal result, both
 * report endpoints). Each method below is typed against the exact shape
 * erp-api/src/controllers/assets.js sends for that route — read the
 * controller before changing a return type here, don't guess.
 *
 * Numeric DECIMAL columns (purchase_cost, gross_block, accumulated_
 * depreciation, salvage_value, rate_pct, opening/closing WDV, ...) come
 * back from mysql2 as strings by default (no `decimalNumbers: true` on
 * the pool) — typed here as `number | string` so callers must parse
 * before doing arithmetic or formatting. Fields computed in JS by
 * depreciationService's pure functions (preview/run lines, disposal,
 * deferred tax, register) ARE real numbers already (round2 returns a
 * JS number), so those are typed as plain `number`.
 */

/** See scmService.ts — a type alias (not interface) so `Record<string, ...>`
 *  structural typing accepts it for the query-string builder below. */
type Filters = Record<string, string | number | boolean | null | undefined>;

const qs = (params?: Filters): string => {
  if (!params) return '';
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
};

// ---------------------------------------------------------------------------
// Enums — mirrored from erp-api/src/models/Asset.js and
// erp-api/src/services/depreciationService.js constants.
// ---------------------------------------------------------------------------

export const ASSET_STATUSES = ['IN_USE', 'IN_STORE', 'UNDER_REPAIR', 'RETIRED', 'DISPOSED'] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export const MAINTENANCE_TYPES = ['PREVENTIVE', 'CORRECTIVE', 'CALIBRATION', 'INSPECTION'] as const;
export type MaintenanceType = (typeof MAINTENANCE_TYPES)[number];

export const DEPRECIATION_METHODS = ['SLM', 'WDV', 'NONE'] as const;
export type DepreciationMethod = (typeof DEPRECIATION_METHODS)[number];

export const BLOCK_CLASSES = ['BUILDING', 'PLANT_MACHINERY', 'FURNITURE', 'INTANGIBLE'] as const;
export type BlockClass = (typeof BLOCK_CLASSES)[number];

export const DEPRECIATION_BOOKS = ['COMPANIES_ACT', 'INCOME_TAX'] as const;
export type DepreciationBook = (typeof DEPRECIATION_BOOKS)[number];

export const DISPOSAL_TYPES = ['SALE', 'SCRAP', 'WRITE_OFF', 'TRANSFER'] as const;
export type DisposalType = (typeof DISPOSAL_TYPES)[number];

export type DepreciationRunStatus = 'DRAFT' | 'POSTED' | 'REVERSED';

// ---------------------------------------------------------------------------
// ASSETS
// ---------------------------------------------------------------------------

/** An `assets` row as Asset.getAssets / getAssetById return it, joined to
 *  its category name and assignee's name. */
export interface Asset {
  id: number;
  tenant_id: number;
  asset_code: string;
  name: string;
  category_id: number | null;
  serial_number: string | null;
  purchase_date: string | null;
  purchase_cost: number | string;
  current_value: number | string;
  warranty_until: string | null;
  status: AssetStatus;
  location: string | null;
  capitalised_on: string | null;
  put_to_use_on: string | null;
  salvage_value: number | string;
  depreciation_method: DepreciationMethod | null;
  useful_life_months: number | null;
  gross_block: number | string;
  accumulated_depreciation: number | string;
  is_cwip: number | boolean;
  it_block_id: number | null;
  source_purchase_invoice_id: number | null;
  source_purchase_invoice_item_id: number | null;
  assigned_to_employee_id: number | null;
  disposed_on: string | null;
  created_at: string;
  updated_at: string;
  category_name: string | null;
  assignee_first_name: string | null;
  assignee_last_name: string | null;
}

export interface AssetMaintenance {
  id: number;
  tenant_id: number;
  asset_id: number;
  maintenance_type: MaintenanceType;
  scheduled_date: string | null;
  cost: number | string;
  notes: string | null;
  completed_date: string | null;
  asset_name?: string;
  asset_code?: string;
}

/** getAssetById additionally folds in the asset's maintenance history. */
export interface AssetDetail extends Asset {
  maintenanceHistory: AssetMaintenance[];
}

export interface AssetListFilters {
  status?: AssetStatus | '';
  categoryId?: number | string;
  assignedTo?: number | string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface AssetPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface CreateAssetInput {
  name: string;
  categoryId?: number | null;
  serialNumber?: string | null;
  purchaseDate?: string | null;
  purchaseCost?: number;
  currentValue?: number;
  warrantyUntil?: string | null;
  status?: AssetStatus;
  location?: string | null;
  assetCode?: string;
  capitalisedOn?: string | null;
  putToUseOn?: string | null;
  salvageValue?: number;
  depreciationMethod?: DepreciationMethod;
  usefulLifeMonths?: number;
  grossBlock?: number;
  isCwip?: boolean;
  itBlockId?: number | null;
}

export type UpdateAssetInput = CreateAssetInput;

export interface AssetSummary {
  totalAssets: number;
  totalValue: number;
  inUse: number;
  underRepair: number;
  warrantyExpiringSoon: number;
  maintenanceDue: number;
}

// ---------------------------------------------------------------------------
// CATEGORIES
// ---------------------------------------------------------------------------

export interface AssetCategory {
  id: number;
  tenant_id: number;
  name: string;
  depreciation_rate: number | string;
  useful_life_years: number | null;
  schedule_ii_class: string | null;
  depreciation_method: DepreciationMethod;
  useful_life_months: number | null;
  salvage_pct: number | string;
  asset_account_id: number | null;
  accum_dep_account_id: number | null;
  dep_expense_account_id: number | null;
  it_block_id: number | null;
}

export interface CreateCategoryInput {
  name: string;
  depreciationRate?: number;
  usefulLifeYears?: number;
}

// ---------------------------------------------------------------------------
// INCOME TAX BLOCKS
// ---------------------------------------------------------------------------

export interface AssetBlock {
  id: number;
  tenant_id: number;
  block_name: string;
  block_class: BlockClass;
  rate_pct: number | string;
}

/** getBlockBalances joins the block's name/class/rate onto every balance row. */
export interface AssetBlockBalance {
  id: number;
  tenant_id: number;
  block_id: number;
  financial_year: string;
  opening_wdv: number | string;
  additions_full_rate: number | string;
  additions_half_rate: number | string;
  deletions: number | string;
  depreciation_amount: number | string;
  closing_wdv: number | string;
  run_id: number | null;
  block_name: string;
  block_class: BlockClass;
  rate_pct: number | string;
}

export interface CreateBlockInput {
  blockName: string;
  blockClass: BlockClass;
  ratePct: number;
}

// ---------------------------------------------------------------------------
// MAINTENANCE
// ---------------------------------------------------------------------------

export interface MaintenanceFilters {
  assetId?: number | string;
  upcoming?: boolean;
}

export interface CreateMaintenanceInput {
  assetId: number;
  maintenanceType?: MaintenanceType;
  scheduledDate?: string;
  cost?: number;
  notes?: string;
}

export interface CompleteMaintenanceInput {
  completedDate?: string;
  cost?: number;
  notes?: string;
}

// ---------------------------------------------------------------------------
// DEPRECIATION
// ---------------------------------------------------------------------------

/** One asset's computed charge for the period — COMPANIES_ACT book only. */
export interface CompaniesActLine {
  assetId: number;
  assetCode: string;
  assetName: string;
  accumDepAccountId: number | null;
  depExpenseAccountId: number | null;
  openingWdv: number;
  depreciationAmount: number;
  closingWdv: number;
  daysInPeriod: number;
  daysHeld: number;
  method: DepreciationMethod;
}

/** One block's computed charge for the FY — INCOME_TAX book only. */
export interface IncomeTaxLine {
  blockId: number;
  blockName: string;
  blockClass: BlockClass;
  ratePct: number;
  openingWdv: number;
  additionsFullRate: number;
  additionsHalfRate: number;
  deletions: number;
  depreciationAmount: number;
  closingWdv: number;
  extinguished: boolean;
  shortTermCapitalGain: number;
}

export type DepreciationLine = CompaniesActLine | IncomeTaxLine;

/** Narrows a preview/run line by book — COMPANIES_ACT lines carry `assetId`,
 *  INCOME_TAX lines carry `blockId`; the two shapes never mix in one response. */
export const isCompaniesActLine = (line: DepreciationLine): line is CompaniesActLine =>
  (line as CompaniesActLine).assetId !== undefined;

export interface DepreciationPreview {
  book: DepreciationBook;
  periodStart: string;
  periodEnd: string;
  /** Only present for INCOME_TAX previews. */
  financialYear?: string;
  lines: DepreciationLine[];
  totalAmount: number;
  assetCount: number;
}

export interface DepreciationRun {
  id: number;
  tenant_id: number;
  book: DepreciationBook;
  period_start: string;
  period_end: string;
  financial_year: string;
  status: DepreciationRunStatus;
  total_amount: number | string;
  asset_count: number;
  run_by: number | null;
  journal_entry_id: number | null;
  posted_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Finance.createJournalEntry's return shape — INCOME_TAX runs never get one. */
export interface JournalEntryRef {
  id: number;
  entryNumber: string;
  entryDate: string;
  description: string;
  reference: string | null;
  status: string;
  totalDebit: number;
  totalCredit: number;
}

export interface CreateRunResult {
  run: DepreciationRun;
  lines: DepreciationLine[];
}

export interface PostRunResult {
  run: DepreciationRun;
  journalEntry: JournalEntryRef | null;
  lines: DepreciationLine[];
}

export interface RunListFilters {
  book?: DepreciationBook | '';
  status?: DepreciationRunStatus | '';
  financialYear?: string;
}

export interface DepreciationScheduleEntry {
  id: number;
  tenant_id: number;
  asset_id: number;
  run_id: number;
  period_start: string;
  period_end: string;
  opening_wdv: number | string;
  depreciation_amount: number | string;
  closing_wdv: number | string;
  method: DepreciationMethod;
  days_in_period: number;
  days_held: number;
  book: DepreciationBook;
  run_status: DepreciationRunStatus;
}

export interface DepreciationSchedule {
  asset: Asset;
  entries: DepreciationScheduleEntry[];
}

// ---------------------------------------------------------------------------
// DISPOSAL
// ---------------------------------------------------------------------------

export interface DisposeAssetInput {
  disposalDate: string;
  disposalType: DisposalType;
  proceeds?: number;
  receiptAccountId?: number;
  notes?: string;
}

export interface AssetDisposal {
  id: number;
  tenant_id: number;
  asset_id: number;
  disposal_date: string;
  disposal_type: DisposalType;
  proceeds: number | string;
  gross_block_at_disposal: number | string;
  accumulated_depreciation_at_disposal: number | string;
  net_book_value: number | string;
  gain_loss: number | string;
  journal_entry_id: number | null;
  notes: string | null;
  disposed_by: number | null;
}

export interface DisposeAssetResult {
  disposal: AssetDisposal;
  journalEntry: JournalEntryRef | null;
  netBookValue: number;
  gainLoss: number;
}

// ---------------------------------------------------------------------------
// REPORTS
// ---------------------------------------------------------------------------

export interface DeferredTaxLine {
  blockId: number;
  blockName: string;
  blockClass: BlockClass;
  bookWdv: number;
  taxWdv: number;
  timingDifference: number;
  amount: number;
  type: 'DTA' | 'DTL' | 'NONE';
  ratePct: number;
}

export interface DeferredTaxReport {
  financialYear: string;
  ratePct: number;
  lines: DeferredTaxLine[];
  totalDTA: number;
  totalDTL: number;
  net: number;
}

export interface FixedAssetRegisterLine {
  assetId: number;
  assetCode: string;
  name: string;
  category: string | null;
  grossOpening: number;
  grossAdditions: number;
  grossDeletions: number;
  grossClosing: number;
  depOpening: number;
  depCharge: number;
  depDeletions: number;
  depClosing: number;
  netBlock: number;
}

export interface FixedAssetRegisterTotals {
  grossOpening: number;
  grossAdditions: number;
  grossDeletions: number;
  grossClosing: number;
  depOpening: number;
  depCharge: number;
  depDeletions: number;
  depClosing: number;
  netBlock: number;
}

export interface FixedAssetRegisterReport {
  financialYear: string;
  startISO: string;
  endISO: string;
  lines: FixedAssetRegisterLine[];
  totals: FixedAssetRegisterTotals;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const assetService = {
  // ── Summary ──────────────────────────────────────────────────────────────
  getSummary: (): Promise<AssetSummary> => api.get('/assets/summary'),

  // ── Assets ───────────────────────────────────────────────────────────────
  getAssets: (filters?: AssetListFilters): Promise<{ assets: Asset[]; pagination: AssetPagination }> =>
    api.get(`/assets${qs(filters as Filters)}`),

  getAsset: (id: number | string): Promise<{ asset: AssetDetail }> => api.get(`/assets/${id}`),

  createAsset: (payload: CreateAssetInput): Promise<{ asset: AssetDetail }> => api.post('/assets', payload),

  updateAsset: (id: number | string, payload: UpdateAssetInput): Promise<{ asset: AssetDetail }> =>
    api.put(`/assets/${id}`, payload),

  updateAssetStatus: (id: number | string, status: AssetStatus): Promise<{ asset: AssetDetail }> =>
    api.patch(`/assets/${id}/status`, { status }),

  assignAsset: (id: number | string, employeeId: number | null): Promise<{ asset: AssetDetail }> =>
    api.patch(`/assets/${id}/assign`, { employeeId }),

  getDepreciationSchedule: (id: number | string): Promise<DepreciationSchedule> =>
    api.get(`/assets/${id}/depreciation-schedule`),

  disposeAsset: (id: number | string, payload: DisposeAssetInput): Promise<DisposeAssetResult> =>
    api.post(`/assets/${id}/dispose`, payload),

  capitaliseAsset: (payload: {
    purchaseInvoiceId: number;
    purchaseInvoiceItemId: number;
    categoryId?: number;
    name?: string;
    grossBlock?: number;
  }): Promise<{ asset: AssetDetail }> => api.post('/assets/capitalise', payload),

  // ── Categories ───────────────────────────────────────────────────────────
  getCategories: (): Promise<{ categories: AssetCategory[]; count: number }> => api.get('/assets/categories'),

  createCategory: (payload: CreateCategoryInput): Promise<{ category: AssetCategory }> =>
    api.post('/assets/categories', payload),

  // ── Income Tax blocks ────────────────────────────────────────────────────
  getBlocks: (): Promise<{ blocks: AssetBlock[]; count: number }> => api.get('/assets/blocks'),

  createBlock: (payload: CreateBlockInput): Promise<{ block: AssetBlock }> => api.post('/assets/blocks', payload),

  getBlockBalances: (financialYear?: string): Promise<{ balances: AssetBlockBalance[]; count: number }> =>
    api.get(`/assets/blocks/balances${qs({ financialYear })}`),

  // ── Maintenance ──────────────────────────────────────────────────────────
  getMaintenance: (filters?: MaintenanceFilters): Promise<{ maintenance: AssetMaintenance[]; count: number }> =>
    api.get(`/assets/maintenance${qs(filters as Filters)}`),

  createMaintenance: (payload: CreateMaintenanceInput): Promise<{ maintenance: AssetMaintenance }> =>
    api.post('/assets/maintenance', payload),

  completeMaintenance: (
    id: number | string,
    payload: CompleteMaintenanceInput
  ): Promise<{ maintenance: AssetMaintenance }> => api.patch(`/assets/maintenance/${id}/complete`, payload),

  // ── Depreciation ─────────────────────────────────────────────────────────
  previewDepreciationRun: (book: DepreciationBook, periodStart: string, periodEnd: string): Promise<DepreciationPreview> =>
    api.post('/assets/depreciation/preview', { book, periodStart, periodEnd }),

  createDepreciationRun: (book: DepreciationBook, periodStart: string, periodEnd: string): Promise<CreateRunResult> =>
    api.post('/assets/depreciation/runs', { book, periodStart, periodEnd }),

  listDepreciationRuns: (filters?: RunListFilters): Promise<{ runs: DepreciationRun[]; count: number }> =>
    api.get(`/assets/depreciation/runs${qs(filters as Filters)}`),

  postDepreciationRun: (id: number | string): Promise<PostRunResult> => api.post(`/assets/depreciation/runs/${id}/post`),

  reverseDepreciationRun: (id: number | string): Promise<{ run: DepreciationRun }> =>
    api.post(`/assets/depreciation/runs/${id}/reverse`),

  // ── Reports ──────────────────────────────────────────────────────────────
  getDeferredTaxReport: (financialYear: string, ratePct?: number): Promise<DeferredTaxReport> =>
    api.get(`/assets/reports/deferred-tax${qs({ financialYear, ratePct })}`),

  getFixedAssetRegister: (financialYear: string): Promise<FixedAssetRegisterReport> =>
    api.get(`/assets/reports/register${qs({ financialYear })}`),

  // ── Lifecycle (migration 067) ────────────────────────────────────────────
  updateCategory: (id: number | string, payload: Record<string, any>): Promise<{ category: AssetCategory }> =>
    api.put(`/assets/categories/${id}`, payload),

  transferAsset: (id: number | string, payload: { transferDate: string; toEmployeeId?: number | null; toLocation?: string | null; toCostCenterId?: number | null; reason?: string | null }): Promise<{ asset: AssetDetail }> =>
    api.post(`/assets/${id}/transfer`, payload),

  getAssetHistory: (id: number | string): Promise<{ assignments: any[]; transfers: any[] }> =>
    api.get(`/assets/${id}/history`),

  impairAsset: (id: number | string, payload: { impairmentDate: string; recoverableAmount: number; reason?: string }): Promise<any> =>
    api.post(`/assets/${id}/impair`, payload),

  revalueAsset: (id: number | string, payload: { revaluationDate: string; revaluedAmount: number; reason?: string }): Promise<any> =>
    api.post(`/assets/${id}/revalue`, payload),

  getGlReconciliation: (): Promise<{ lines: Array<{ label: string; accountCode: string; register: number; gl: number; difference: number; matched: boolean }>; allMatched: boolean }> =>
    api.get('/assets/reports/gl-reconciliation'),

  // ── Physical verification ────────────────────────────────────────────────
  listVerificationCampaigns: (): Promise<{ campaigns: any[]; count: number }> => api.get('/assets/verification'),
  createVerificationCampaign: (payload: { name: string; campaignDate: string; location?: string }): Promise<{ campaign: any }> =>
    api.post('/assets/verification', payload),
  getVerificationSummary: (id: number | string): Promise<{ campaign: any; summary: Record<string, number>; verified: number; counts: any[] }> =>
    api.get(`/assets/verification/${id}`),
  recordVerificationCounts: (id: number | string, counts: Array<{ assetId: number; expected?: boolean; foundStatus?: string; remarks?: string }>): Promise<any> =>
    api.post(`/assets/verification/${id}/counts`, { counts }),
};

export default assetService;
